import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  MessageSquare,
  Home,
  Trash2,
  Eye,
  Mail,
  Phone,
  CheckCircle,
  Circle,
  RefreshCw,
  Star,
  ThumbsUp,
  ThumbsDown,
  Lock,
  LogOut,
  Pencil,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [submissions, setSubmissions] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [activeTab, setActiveTab] = useState("enquiries");
  const [editingReview, setEditingReview] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", relation: "", content: "", rating: 5 });
  const [savingEdit, setSavingEdit] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = sessionStorage.getItem("adminToken");
      if (token) {
        try {
          await axios.get(`${API}/admin/verify?token=${token}`);
          setIsAuthenticated(true);
        } catch {
          sessionStorage.removeItem("adminToken");
        }
      }
      setIsLoading(false);
    };
    checkAuth();
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchSubmissions();
      fetchPendingReviews();
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("adminToken", response.data.token);
      setIsAuthenticated(true);
      toast.success("Welcome to Admin Panel");
    } catch (error) {
      setLoginError("Invalid password");
    }
  };

  const handleLogout = () => {
    const token = sessionStorage.getItem("adminToken");
    if (token) {
      axios.post(`${API}/admin/logout?token=${token}`).catch(() => {});
    }
    sessionStorage.removeItem("adminToken");
    setIsAuthenticated(false);
    toast.success("Logged out successfully");
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contact`);
      setSubmissions(response.data);
    } catch (error) {
      console.error("Error fetching submissions:", error);
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingReviews = async () => {
    setReviewsLoading(true);
    try {
      const response = await axios.get(`${API}/testimonials/pending`);
      setPendingReviews(response.data);
    } catch (error) {
      console.error("Error fetching pending reviews:", error);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await axios.patch(`${API}/contact/${id}/read`);
      setSubmissions((prev) =>
        prev.map((sub) => (sub.id === id ? { ...sub, is_read: true } : sub))
      );
      toast.success("Marked as read");
    } catch (error) {
      console.error("Error marking as read:", error);
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/contact/${id}`);
      setSubmissions((prev) => prev.filter((sub) => sub.id !== id));
      setSelectedSubmission(null);
      toast.success("Submission deleted");
    } catch (error) {
      console.error("Error deleting submission:", error);
      toast.error("Failed to delete");
    }
  };

  const handleApproveReview = async (id) => {
    try {
      await axios.patch(`${API}/testimonials/${id}/approve`);
      setPendingReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Review approved and published");
    } catch (error) {
      console.error("Error approving review:", error);
      toast.error("Failed to approve");
    }
  };

  const handleRejectReview = async (id) => {
    try {
      await axios.delete(`${API}/testimonials/${id}`);
      setPendingReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Review rejected and deleted");
    } catch (error) {
      console.error("Error rejecting review:", error);
      toast.error("Failed to reject");
    }
  };

  const openEditReview = (review) => {
    setEditingReview(review);
    setEditForm({
      name: review.name || "",
      relation: review.relation || "",
      content: review.content || "",
      rating: review.rating || 5,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingReview) return;
    setSavingEdit(true);
    try {
      const response = await axios.patch(
        `${API}/testimonials/${editingReview.id}`,
        editForm
      );
      setPendingReviews((prev) =>
        prev.map((r) => (r.id === editingReview.id ? response.data : r))
      );
      toast.success("Review updated");
      setEditingReview(null);
    } catch (error) {
      console.error("Error updating review:", error);
      toast.error("Failed to save changes");
    } finally {
      setSavingEdit(false);
    }
  };

  const unreadCount = submissions.filter((s) => !s.is_read).length;

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-white border border-primary/10 rounded-2xl p-8 shadow-lg">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-primary" />
              </div>
              <h1 className="font-serif text-2xl font-semibold text-primary">Admin Login</h1>
              <p className="text-muted-foreground mt-2">Enter your password to continue</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="h-12 rounded-lg"
                  data-testid="admin-password-input"
                />
              </div>

              {loginError && (
                <p className="text-sm text-destructive">{loginError}</p>
              )}

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-white rounded-full py-6"
                data-testid="admin-login-btn"
              >
                Login
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/" className="text-sm text-muted-foreground hover:text-primary">
                ← Back to Website
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div data-testid="admin-page" className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-primary text-white p-6 hidden lg:block">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
            <span className="font-serif font-bold text-lg">K</span>
          </div>
          <div>
            <h2 className="font-serif font-semibold">Konastone</h2>
            <p className="text-xs text-white/60">Admin Panel</p>
          </div>
        </div>

        <nav className="space-y-2">
          <button
            onClick={() => setActiveTab("enquiries")}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === "enquiries" ? "bg-white/10" : "text-white/70 hover:text-white hover:bg-white/5"
            }`}
          >
            <MessageSquare className="w-5 h-5" />
            <span className="font-medium">Consultations</span>
            {unreadCount > 0 && (
              <Badge className="ml-auto bg-accent text-white">{unreadCount}</Badge>
            )}
          </button>
          <button
            onClick={() => setActiveTab("reviews")}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === "reviews" ? "bg-white/10" : "text-white/70 hover:text-white hover:bg-white/5"
            }`}
          >
            <Star className="w-5 h-5" />
            <span className="font-medium">Reviews</span>
            {pendingReviews.length > 0 && (
              <Badge className="ml-auto bg-yellow-500 text-white">{pendingReviews.length}</Badge>
            )}
          </button>
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
          >
            <Home className="w-5 h-5" />
            <span>Back to Website</span>
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition-colors mt-4"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-10">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between mb-6">
          <div className="flex gap-2">
            <Button
              variant={activeTab === "enquiries" ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab("enquiries")}
            >
              Consultations {unreadCount > 0 && `(${unreadCount})`}
            </Button>
            <Button
              variant={activeTab === "reviews" ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab("reviews")}
            >
              Reviews {pendingReviews.length > 0 && `(${pendingReviews.length})`}
            </Button>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4" />
          </Button>
        </div>

        {/* Enquiries Tab */}
        {activeTab === "enquiries" && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <div>
                <h1 className="font-serif text-2xl md:text-3xl font-semibold text-primary">
                  Care Consultation Requests
                </h1>
                <p className="text-muted-foreground">
                  Manage booking requests and enquiries from the contact form
                </p>
              </div>
              <Button
                variant="outline"
                onClick={fetchSubmissions}
                className="rounded-full"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-white border border-primary/10 rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-serif font-bold text-primary">{submissions.length}</p>
                    <p className="text-sm text-muted-foreground">Total</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-primary/10 rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center">
                    <Circle className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <p className="text-2xl font-serif font-bold text-accent">{unreadCount}</p>
                    <p className="text-sm text-muted-foreground">Unread</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-primary/10 rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-serif font-bold text-green-600">{submissions.length - unreadCount}</p>
                    <p className="text-sm text-muted-foreground">Read</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-primary/10 rounded-xl overflow-hidden">
              {loading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
                  <p className="text-muted-foreground">Loading...</p>
                </div>
              ) : submissions.length === 0 ? (
                <div className="p-12 text-center">
                  <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-serif text-xl font-semibold text-primary mb-2">No Enquiries Yet</h3>
                  <p className="text-muted-foreground">Contact form submissions will appear here.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">Status</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead className="hidden md:table-cell">Email</TableHead>
                        <TableHead className="hidden lg:table-cell">Service</TableHead>
                        <TableHead className="hidden md:table-cell">Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {submissions.map((submission) => (
                        <TableRow key={submission.id} className={!submission.is_read ? "bg-accent/5" : ""}>
                          <TableCell>
                            {submission.is_read ? (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : (
                              <Circle className="w-5 h-5 text-accent fill-accent" />
                            )}
                          </TableCell>
                          <TableCell className="font-medium">{submission.name}</TableCell>
                          <TableCell className="hidden md:table-cell text-muted-foreground">{submission.email}</TableCell>
                          <TableCell className="hidden lg:table-cell">{submission.service_interest || "-"}</TableCell>
                          <TableCell className="hidden md:table-cell text-muted-foreground">
                            {new Date(submission.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedSubmission(submission);
                                  if (!submission.is_read) handleMarkAsRead(submission.id);
                                }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Enquiry?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently delete the enquiry from {submission.name}.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDelete(submission.id)} className="bg-destructive hover:bg-destructive/90">
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Reviews Tab */}
        {activeTab === "reviews" && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <div>
                <h1 className="font-serif text-2xl md:text-3xl font-semibold text-primary">Review Moderation</h1>
                <p className="text-muted-foreground">Approve or reject visitor-submitted reviews</p>
              </div>
              <Button variant="outline" onClick={fetchPendingReviews} className="rounded-full">
                <RefreshCw className={`w-4 h-4 mr-2 ${reviewsLoading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>

            <div className="bg-white border border-primary/10 rounded-xl overflow-hidden">
              {reviewsLoading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
                  <p className="text-muted-foreground">Loading...</p>
                </div>
              ) : pendingReviews.length === 0 ? (
                <div className="p-12 text-center">
                  <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-serif text-xl font-semibold text-primary mb-2">No Pending Reviews</h3>
                  <p className="text-muted-foreground">New visitor reviews will appear here for your approval.</p>
                </div>
              ) : (
                <div className="divide-y divide-primary/10">
                  {pendingReviews.map((review) => (
                    <div key={review.id} className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                              <span className="font-serif font-bold text-primary">{review.name.charAt(0)}</span>
                            </div>
                            <div>
                              <p className="font-semibold text-primary">{review.name}</p>
                              <p className="text-sm text-muted-foreground">{review.relation}</p>
                            </div>
                            <div className="flex ml-2">
                              {[...Array(review.rating)].map((_, i) => (
                                <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                              ))}
                            </div>
                          </div>
                          <p className="text-muted-foreground mb-2">"{review.content}"</p>
                          <p className="text-xs text-muted-foreground">{review.date}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => openEditReview(review)}
                            variant="outline"
                            size="sm"
                            data-testid={`edit-review-btn-${review.id}`}
                          >
                            <Pencil className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button onClick={() => handleApproveReview(review.id)} className="bg-green-600 hover:bg-green-700 text-white" size="sm" data-testid={`approve-review-btn-${review.id}`}>
                            <ThumbsUp className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="text-destructive border-destructive hover:bg-destructive/10">
                                <ThumbsDown className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Reject Review?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete the review from {review.name}.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleRejectReview(review.id)} className="bg-destructive hover:bg-destructive/90">
                                  Reject & Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Detail Modal */}
        {selectedSubmission && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedSubmission(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl p-6 md:p-8 max-w-lg w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="font-serif text-xl font-semibold text-primary">{selectedSubmission.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {new Date(selectedSubmission.created_at).toLocaleDateString("en-GB", {
                      day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit"
                    })}
                  </p>
                </div>
                <Badge variant={selectedSubmission.is_read ? "secondary" : "default"}>
                  {selectedSubmission.is_read ? "Read" : "New"}
                </Badge>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                  <a href={`mailto:${selectedSubmission.email}`} className="text-primary hover:underline">
                    {selectedSubmission.email}
                  </a>
                </div>
                {selectedSubmission.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-muted-foreground" />
                    <a href={`tel:${selectedSubmission.phone}`} className="text-primary hover:underline">
                      {selectedSubmission.phone}
                    </a>
                  </div>
                )}
                {selectedSubmission.service_interest && (
                  <div className="flex items-center gap-3">
                    <LayoutDashboard className="w-5 h-5 text-muted-foreground" />
                    <span>{selectedSubmission.service_interest}</span>
                  </div>
                )}
              </div>

              <div className="bg-muted/50 rounded-xl p-4 mb-6">
                <p className="text-sm font-medium text-primary mb-2">Message:</p>
                <p className="text-muted-foreground whitespace-pre-wrap">{selectedSubmission.message}</p>
              </div>

              <div className="flex gap-3">
                <Button onClick={() => setSelectedSubmission(null)} variant="outline" className="flex-1 rounded-full">
                  Close
                </Button>
                <a href={`mailto:${selectedSubmission.email}`} className="flex-1">
                  <Button className="w-full bg-primary hover:bg-primary/90 rounded-full">
                    <Mail className="w-4 h-4 mr-2" />
                    Reply
                  </Button>
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Edit Review Dialog */}
        <Dialog open={!!editingReview} onOpenChange={(open) => !open && setEditingReview(null)}>
          <DialogContent className="max-w-lg" data-testid="edit-review-dialog">
            <DialogHeader>
              <DialogTitle>Edit Pending Review</DialogTitle>
              <DialogDescription>
                Fix typos or adjust details before approving. Changes are saved to the pending review.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Name</Label>
                  <Input
                    id="edit-name"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    data-testid="edit-review-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-relation">Relationship</Label>
                  <Input
                    id="edit-relation"
                    value={editForm.relation}
                    onChange={(e) => setEditForm({ ...editForm, relation: e.target.value })}
                    data-testid="edit-review-relation-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Rating</Label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setEditForm({ ...editForm, rating: star })}
                      className="p-1"
                      data-testid={`edit-review-star-${star}`}
                    >
                      <Star
                        className={`w-7 h-7 ${
                          star <= editForm.rating
                            ? "text-yellow-400 fill-current"
                            : "text-gray-300"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-content">Review</Label>
                <Textarea
                  id="edit-content"
                  rows={5}
                  value={editForm.content}
                  onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                  data-testid="edit-review-content-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setEditingReview(null)}
                disabled={savingEdit}
                data-testid="edit-review-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={savingEdit || !editForm.name || !editForm.content}
                className="bg-primary hover:bg-primary/90"
                data-testid="edit-review-save-btn"
              >
                {savingEdit ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};
